import { format } from "date-fns";

import {
  DOWNLOAD_OUTCOME,
  downloadFromUrl,
  extensionFromUrl,
  toSafeFileName,
} from "@/lib/download-file";

import type { CaptureItem } from "./use-broadcasts";

/**
 * Downloading Broadcast files — Snapshots rows (single and bulk) and Run history PDFs.
 *
 * The bulk Download button used to call `window.open()` once per selected row. A browser grants a
 * single popup per user gesture, so picking five snapshots opened exactly one tab and silently
 * dropped the other four (#82 — scio3#2149 was the same defect). Each file is now fetched and saved
 * instead; see `@/lib/download-file` for how, and for why that fetch needs the bucket to allow CORS.
 *
 * This module keeps only what is Broadcast-specific: how files are named, and how a multi-row
 * selection is downloaded and counted.
 */

/**
 * `Morning digest - 2026-09-22.pdf` — the one naming rule for Broadcast files, so a file saved from
 * Snapshots and the same file saved from Run history get the same name (and the same name as in
 * OptiSigns). The date is the viewer's local date, the one the tables show — not the UTC date an
 * ISO string would give if sliced.
 */
export function artifactFileName(input: {
  broadcastName?: string | null;
  date?: string | null;
  url?: string | null;
  /** Used when the URL carries no extension. */
  fallbackExtension: "pdf" | "png";
}): string {
  const parsed = input.date ? new Date(input.date) : null;
  const date =
    parsed && !Number.isNaN(parsed.getTime())
      ? format(parsed, "yyyy-MM-dd")
      : "";
  const extension =
    (input.url ? extensionFromUrl(input.url) : undefined) ??
    input.fallbackExtension;
  const base = [input.broadcastName?.trim() || "capture", date]
    .filter(Boolean)
    .join(" - ");
  // A broadcast name is free text; Windows and macOS both reject some of its characters.
  return `${toSafeFileName(base)}.${extension}`;
}

/** A Snapshots row is named by the moment it was captured. */
export function captureFileName(capture: CaptureItem): string {
  return artifactFileName({
    broadcastName: capture.broadcastName,
    date: capture.capturedAt,
    url: capture.artifactUrl,
    fallbackExtension: capture.kind === "pdf" ? "pdf" : "png",
  });
}

/** Outcome of a bulk download, so the caller can tell the user what actually happened. */
export interface DownloadCapturesResult {
  downloaded: number;
  /** Selected rows whose file is already gone (retention-swept) — nothing to fetch. */
  unavailable: number;
  /** Rows that had a URL but could not be fetched; each was opened in a tab instead. */
  failed: number;
}

/**
 * How many files to fetch at once. One at a time costs the full round trip per file, so a dozen
 * captures crawl; all at once is no faster past ~6 (browsers cap connections per host there) and
 * would hold every blob in memory at the same time. Six lanes buys the speed without the memory.
 */
const DOWNLOAD_CONCURRENCY = 6;

/** Download every capture that still has a file, six at a time. */
export async function downloadCaptures(
  rows: readonly CaptureItem[],
): Promise<DownloadCapturesResult> {
  const result: DownloadCapturesResult = {
    downloaded: 0,
    unavailable: 0,
    failed: 0,
  };
  let next = 0;

  // Each lane pulls the next row itself, so a slow file holds up only its own lane. `next++` is safe
  // without a lock: JS runs one task at a time, and nothing awaits between the read and the write.
  async function lane(): Promise<void> {
    while (next < rows.length) {
      await downloadOne(rows[next++], result);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(DOWNLOAD_CONCURRENCY, rows.length) }, lane),
  );
  return result;
}

async function downloadOne(
  row: CaptureItem,
  result: DownloadCapturesResult,
): Promise<void> {
  if (!row.artifactUrl) {
    result.unavailable += 1;
    return;
  }
  const outcome = await downloadFromUrl(row.artifactUrl, captureFileName(row));
  if (outcome === DOWNLOAD_OUTCOME.SAVED) result.downloaded += 1;
  else result.failed += 1;
}
